import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  MdAccountTree,
  MdFilterListOff,
  MdRefresh,
} from 'react-icons/md';
import { Page } from '@components/layout/Page';
import { PageEmpty } from '@components/layout/PageEmpty';
import { DataPagination } from '@components/ui/DataPagination';
import { GridSkeleton } from '@components/ui/GridSkeleton';
import { TabFilter } from '@components/ui/TabFilter';
import { PriceSeriesEditDialog } from '@features/priceSeries/components/PriceSeriesEditDialog';
import { PriceSeriesGrid } from '@features/priceSeries/components/PriceSeriesGrid';
import { useHasAnyRole } from '@features/auth/hooks';
import { usePriceSeries } from '@features/priceSeries/hooks';
import { useApmcOptions } from '@features/apmcs/hooks';
import type { ApmcOption } from '@features/apmcs/schema';
import { useCropOptions } from '@features/crops/hooks';
import type { CropOption } from '@features/crops/schema';
import { useVarietyOptions } from '@features/varieties/hooks';
import type { VarietyOption } from '@features/varieties/schema';
import {
  type ListPriceSeriesParams,
  type PriceSeries,
  type PriceSeriesSortKey,
  type SortDirection,
} from '@features/priceSeries/schema';

const DEFAULT_PAGE_SIZE = 50;

type ActiveTabValue = '' | 'true' | 'false';

const ACTIVE_TABS: ReadonlyArray<{ value: ActiveTabValue; label: string }> = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

export default function PriceSeriesScreen() {
  const canEdit = useHasAnyRole('admin', 'tech_team');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [activeTab, setActiveTab] = useState<ActiveTabValue>('');
  const [cropId, setCropId] = useState<number | null>(null);
  const [varietyId, setVarietyId] = useState<number | null>(null);
  const [apmcId, setApmcId] = useState<number | null>(null);
  const [sort, setSort] = useState<{
    sortBy?: PriceSeriesSortKey;
    sortDir?: SortDirection;
  }>({});
  const [editingRow, setEditingRow] = useState<PriceSeries | null>(null);

  // Debounce search input.
  useEffect(() => {
    const t = setTimeout(() => setSearchText(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to page 1 whenever filters / sort / tab change.
  useEffect(() => {
    setPage(1);
  }, [searchText, activeTab, cropId, varietyId, apmcId, pageSize, sort]);

  const params = useMemo<ListPriceSeriesParams>(
    () => ({
      page,
      pageSize,
      searchText: searchText || undefined,
      cropId: cropId ?? undefined,
      varietyId: varietyId ?? undefined,
      apmcId: apmcId ?? undefined,
      isActive: activeTab || undefined,
      sortBy: sort.sortBy,
      sortDir: sort.sortDir,
    }),
    [page, pageSize, searchText, cropId, varietyId, apmcId, activeTab, sort],
  );

  const { data, isLoading, isFetching, refetch } = usePriceSeries(params);
  const cropOptionsQuery = useCropOptions();
  const apmcOptionsQuery = useApmcOptions();
  const varietyOptionsQuery = useVarietyOptions();
  const filterOptionsLoading =
    cropOptionsQuery.isLoading ||
    apmcOptionsQuery.isLoading ||
    varietyOptionsQuery.isLoading;

  // Wrap each fallback in useMemo so the empty-array reference is stable
  // across renders — without this, the dependent useMemo blocks below
  // (varietyOptions, cropById, etc.) recompute every render even when the
  // query data hasn't changed.
  const crops: CropOption[] = useMemo(
    () => cropOptionsQuery.data ?? [],
    [cropOptionsQuery.data],
  );
  const apmcs: ApmcOption[] = useMemo(
    () => apmcOptionsQuery.data ?? [],
    [apmcOptionsQuery.data],
  );
  const allVarieties: VarietyOption[] = useMemo(
    () => varietyOptionsQuery.data ?? [],
    [varietyOptionsQuery.data],
  );

  // Variety options scoped to the picked crop. Without a crop, the dropdown
  // is disabled — picking a variety in isolation is meaningless (varieties
  // belong to a crop) and would also produce a huge flat list.
  const varietyOptions = useMemo<VarietyOption[]>(
    () =>
      cropId == null
        ? []
        : allVarieties.filter((v) => v.cropId === String(cropId)),
    [allVarieties, cropId],
  );
  const varietyDisabled = cropId == null;

  const cropById = useMemo(
    () => new Map(crops.map((c) => [c.id, c])),
    [crops],
  );
  const apmcById = useMemo(
    () => new Map(apmcs.map((a) => [a.id, a])),
    [apmcs],
  );
  const varietyById = useMemo(
    () => new Map(varietyOptions.map((v) => [v.id, v])),
    [varietyOptions],
  );

  const cropValue: CropOption | null =
    cropId == null ? null : cropById.get(String(cropId)) ?? null;
  const apmcValue: ApmcOption | null =
    apmcId == null ? null : apmcById.get(String(apmcId)) ?? null;
  const varietyValue: VarietyOption | null =
    varietyId == null ? null : varietyById.get(String(varietyId)) ?? null;

  const totalRecords = data?.paginationDetails?.totalRecords ?? 0;
  const hasActiveFilters = Boolean(
    searchText || activeTab || cropId !== null || varietyId !== null || apmcId !== null,
  );
  const isInitialLoad = isLoading && !data;
  const showEmpty = !isLoading && (data?.rowData?.length ?? 0) === 0;

  const clearFilters = () => {
    setSearchInput('');
    setSearchText('');
    setActiveTab('');
    setCropId(null);
    setVarietyId(null);
    setApmcId(null);
  };

  // Wrapper kept as a function so the disabled-only Tooltip doesn't pollute
  // the enabled control with an empty-title popper that intercepts hovers.
  const variety = (
    <Autocomplete
      size="small"
      options={varietyOptions}
      loading={filterOptionsLoading}
      disabled={varietyDisabled}
      value={varietyValue}
      onChange={(_, opt) => setVarietyId(opt ? Number(opt.id) : null)}
      getOptionLabel={(opt) => opt.name}
      isOptionEqualToValue={(opt, v) => opt.id === v.id}
      noOptionsText="No varieties for this crop"
      className="w-full md:w-[200px]"
      // Lock-icon startAdornment dropped — see MarketPriceFiltersBar for the
      // reasoning. Disabled state + Tooltip hover hint communicate the cascade.
      renderInput={(p) => <TextField {...p} label="Variety" />}
    />
  );

  return (
    <Page
      title="Price series"
      subtitle="The switchboard of (APMC × crop × variety) tuples the platform tracks."
      fullHeight
      headerRightSlot={
        <TabFilter
          options={ACTIVE_TABS}
          current={activeTab}
          onChange={setActiveTab}
          ariaLabel="Filter by active state"
        />
      }
    >
      <div className="mb-3 flex flex-col gap-3 rounded-xl bg-surface-muted px-3 py-3 md:flex-row md:flex-wrap md:items-end md:px-4">
        <TextField
          label="Search notes"
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-full md:min-w-[220px] md:flex-1"
        />
        <Autocomplete
          size="small"
          options={crops}
          loading={filterOptionsLoading}
          value={cropValue}
          onChange={(_, opt) => {
            setCropId(opt ? Number(opt.id) : null);
            // Crop change invalidates the picked variety.
            setVarietyId(null);
          }}
          getOptionLabel={(opt) => opt.name}
          isOptionEqualToValue={(opt, v) => opt.id === v.id}
          className="w-full md:w-[200px]"
          renderInput={(p) => <TextField {...p} label="Crop" />}
        />
        {varietyDisabled ? (
          <Tooltip title="Pick a crop first to enable Variety" arrow>
            <span className="block w-full md:inline-block md:w-auto">{variety}</span>
          </Tooltip>
        ) : (
          variety
        )}
        <Autocomplete
          size="small"
          options={apmcs}
          loading={filterOptionsLoading}
          value={apmcValue}
          onChange={(_, opt) => setApmcId(opt ? Number(opt.id) : null)}
          getOptionLabel={(opt) => opt.name}
          isOptionEqualToValue={(opt, v) => opt.id === v.id}
          className="w-full md:w-[240px]"
          renderInput={(p) => <TextField {...p} label="APMC" />}
        />
        <div className="flex items-end gap-2 md:contents">
          <TextField
            label="Page size"
            size="small"
            select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="flex-1 md:w-[110px] md:flex-initial"
          >
            {[25, 50, 100].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>

          <div className="flex items-center gap-2 md:ml-auto">
            {hasActiveFilters && (
              <Button variant="text" size="small" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
            <Tooltip title={isFetching ? 'Loading…' : 'Refresh'}>
              <span>
                <IconButton onClick={() => refetch()} size="small" disabled={isFetching}>
                  {isFetching ? <CircularProgress size={16} thickness={5} /> : <MdRefresh />}
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 overflow-hidden rounded-xl border border-border bg-surface">
        {isFetching && data && (
          <LinearProgress
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 5,
              height: 2,
            }}
          />
        )}
        {isInitialLoad ? (
          <GridSkeleton
            rows={6}
            columns={[
              { width: '1.5fr', bar: 'w-3/4' },
              { width: '1fr', bar: 'w-2/3' },
              { width: '1fr', bar: 'w-2/3' },
              { width: '110px', bar: 'w-14' },
              { width: '100px', bar: 'w-8' },
              { width: '150px', bar: 'w-10' },
              { width: '140px', bar: 'w-10' },
              { width: '2fr', bar: 'w-1/2' },
              { width: '200px', bar: 'w-24' },
              { width: '100px', empty: true },
            ]}
          />
        ) : showEmpty ? (
          hasActiveFilters ? (
            <PageEmpty
              icon={MdFilterListOff}
              title="No matches"
              message="No price series match the current filters. Try clearing crop / variety / APMC or widening your search."
              action={
                <Button variant="outlined" size="small" onClick={clearFilters}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <PageEmpty
              icon={MdAccountTree}
              title="No price series yet"
              message="Series are seeded by SQL or ETL today. Once provisioned, they'll appear here for management."
            />
          )
        ) : (
          <PriceSeriesGrid
            data={data}
            onEdit={setEditingRow}
            onSortChange={setSort}
            canEdit={canEdit}
          />
        )}
      </div>

      <DataPagination
        page={page}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={setPage}
      />

      <PriceSeriesEditDialog
        open={!!editingRow && canEdit}
        row={editingRow}
        onClose={() => setEditingRow(null)}
      />
    </Page>
  );
}
